/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { resolveSegmentKey } from "@/helpers/templates";
import { appLocales, Locales } from "@/lib/i18n";
import { CategorieDocumentWithChildren } from "@/lib/prismic/categories";
import clsx from "clsx";
import Link from "next/link";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

interface CategoryLinkProps {
  category: CategorieDocumentWithChildren;
  currentHandle?: string;
  handle?: string;
  lang?: Locales;
  parent?: CategorieDocumentWithChildren | null;
  isChild?: boolean;
}

const CategoryLink = ({
  category,
  currentHandle = "",
  handle = "/categories",
  lang = "fr",
  parent,
  isChild = false,
}: CategoryLinkProps) => {
  // Fonction pour vérifier si un enfant ou descendant est actif
  const hasActiveChild = (cat: any): boolean => {
    if (!cat.children || cat.children.length === 0) return false;

    return cat.children.some((child: any) => currentHandle === child.uid || hasActiveChild(child));
  };

  // isOpen est true si le parent est actif, si un enfant est actif, sinon état local
  const [localIsOpen, setLocalIsOpen] = useState<boolean>(false);

  if (!category) return null;

  if (lang !== appLocales.defaultLocale) {
    const segment = resolveSegmentKey(lang, "categories");

    if (segment && handle === "/categories") {
      handle = "/" + lang + "/" + segment;
    }
  }

  if (parent && parent.id !== category.id) {
    handle += `/${parent.uid}`;
  }

  handle += "/" + category?.uid;

  const isCurrent = currentHandle === category?.uid;
  const isOpen = isCurrent || hasActiveChild(category) || localIsOpen;

  return (
    <li
      className={clsx(
        "nav-item list-none py-[0.625rem]",
        !isChild
          ? "border-t border-t-[rgba(156,156,156,0.14)] last:border-b last:border-b-[rgba(156,156,156,0.14)]"
          : ""
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <Link
          href={handle}
          className={twMerge(
            "text-sm uppercase hover:underline",
            isCurrent ? "is-active-handle text-pink-primary font-semibold" : "[&:has(a.is-active-handle)]:underline"
          )}
        >
          {category.data.title}
        </Link>

        {category?.children && category.children.length > 0 && (
          <button
            onClick={() => setLocalIsOpen((prev) => !prev)}
            className={clsx("cursor-pointer text-[#0093D0] transition", isOpen && "rotate-180")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="11"
              fill="none"
              viewBox="0 0 16 11"
              aria-hidden="true"
            >
              <path
                fill="#0093D0"
                d="M8.06 9.725c-.277 0-.544-.093-.754-.26l-.087-.077L.883 3.233A1.1 1.1 0 0 1 .629 2a1.15 1.15 0 0 1 .434-.512c.194-.127.425-.194.662-.195l.118.005c.234.023.455.113.636.256l.086.076v.001l5.216 5.067.28.271.278-.271 5.217-5.067.005-.005q.164-.165.383-.257a1.23 1.23 0 0 1 .924-.008 1.143 1.143 0 0 1 .596.522l.05.103q.065.157.082.325l.004.111a1.1 1.1 0 0 1-.278.723l-.079.083-.006.005-6.336 6.155a1.2 1.2 0 0 1-.84.337"
              ></path>
              <path
                stroke="#fff"
                strokeWidth="0.8"
                d="M8.06 9.725c-.277 0-.544-.093-.754-.26l-.087-.077L.883 3.233A1.1 1.1 0 0 1 .629 2a1.15 1.15 0 0 1 .434-.512c.194-.127.425-.194.662-.195l.118.005c.234.023.455.113.636.256l.086.076v.001l5.216 5.067.28.271.278-.271 5.217-5.067.005-.005q.164-.165.383-.257a1.23 1.23 0 0 1 .924-.008 1.143 1.143 0 0 1 .596.522l.05.103q.065.157.082.325l.004.111a1.1 1.1 0 0 1-.278.723l-.079.083-.006.005-6.336 6.155a1.2 1.2 0 0 1-.84.337Z"
              ></path>
              <path
                stroke="#fff"
                strokeWidth="0.8"
                d="M8.06 9.725c-.277 0-.544-.093-.754-.26l-.087-.077L.883 3.233A1.1 1.1 0 0 1 .629 2a1.15 1.15 0 0 1 .434-.512c.194-.127.425-.194.662-.195l.118.005c.234.023.455.113.636.256l.086.076v.001l5.216 5.067.28.271.278-.271 5.217-5.067.005-.005q.164-.165.383-.257a1.23 1.23 0 0 1 .924-.008 1.143 1.143 0 0 1 .596.522l.50.103q.065.157.082.325l.004.111a1.1 1.1 0 0 1-.278.723l-.079.083-.006.005-6.336 6.155a1.2 1.2 0 0 1-.84.337Z"
              ></path>
              <path
                stroke="#fff"
                strokeWidth="0.8"
                d="M8.06 9.725c-.277 0-.544-.093-.754-.26l-.087-.077L.883 3.233A1.1 1.1 0 0 1 .629 2a1.15 1.15 0 0 1 .434-.512c.194-.127.425-.194.662-.195l.118.005c.234.023.455.113.636.256l.086.076v.001l5.216 5.067.28.271.278-.271 5.217-5.067.005-.005q.164-.165.383-.257a1.23 1.23 0 0 1 .924-.008 1.143 1.143 0 0 1 .596.522l.05.103q.065.157.082.325l.004.111a1.1 1.1 0 0 1-.278.723l-.079.083-.006.005-6.336 6.155a1.2 1.2 0 0 1-.84.337Z"
              ></path>
            </svg>
          </button>
        )}
      </div>

      {category?.children && category.children.length > 0 && (
        <ul className={twMerge("my-2 pl-4", isOpen ? "block" : "hidden")}>
          {category.children.map((child) => (
            <CategoryLink
              key={child.id}
              category={child}
              handle={handle}
              currentHandle={currentHandle}
              lang={lang}
              isChild={true}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default CategoryLink;
